'use client';

import { useEffect, useRef, useState } from 'react';

import { useMountTransition } from '@/app/hooks/useMountTransition';
import type { NavItemType } from '@/app/types/top-navigation';
import { MenuItem } from '@/components/layouts/top-navigation/shared/menu-item';
import { Box } from '@/components/shared/layout/box';

interface DesktopSubMenuProps {
  isOpen: boolean;
  activeItem?: NavItemType;
  onClose?: () => void;
}

export const DesktopSubMenu = ({
  isOpen,
  activeItem,
  onClose,
}: DesktopSubMenuProps) => {
  const [currentActive, setCurrentActive] = useState<NavItemType | undefined>(
    undefined
  );
  const [isHoverActive, setIsHoverActive] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { isTransitioning, transitionState } = useMountTransition({
    isMount: isOpen,
    ref: menuRef,
  });

  const closeActiveMenu = () => {
    typeof onClose === 'function' && onClose();
    setIsHoverActive(false);
  };

  // Note: To still show active item on closing even though the `activeItem` is removed
  useEffect(() => {
    if (!!activeItem) {
      setCurrentActive(activeItem);
      return;
    }

    if (!activeItem && !isTransitioning) {
      setCurrentActive(undefined);
    }
  }, [activeItem, isTransitioning]);

  if (!isTransitioning || !currentActive) {
    return null;
  }

  return (
    <>
      {transitionState !== 'exit' && (
        <Box
          position="fixed"
          inset="0"
          backgroundColor="backdrop"
          role="presentation"
          css={{
            /**
             * Note: (will delete)
             * 800ms because when backdrop appears the same at time as the menu background
             * it cause the black color flick a little bit because the menu background also animate from opacity-0
             * so the backdrop need to be animate slower than to make it can't see backdrop when start animating
             */
            transition:
              transitionState === 'animate'
                ? 'opacity 0.5s ease'
                : 'opacity 0.3s ease',
            opacity: transitionState === 'animate' ? 1 : 0,
            backdropFilter: 'blur(4px)',
          }}
          onClick={closeActiveMenu}
          onMouseEnter={!isHoverActive ? undefined : closeActiveMenu}
        />
      )}
      <Box
        ref={menuRef}
        component="aside"
        position="absolute"
        top="0"
        backgroundColor="white"
        paddingTop="24"
        paddingBottom="10"
        width="full"
        css={{
          minHeight: 376,
          transition: 'opacity 0.3s ease',
          opacity: transitionState === 'animate' ? 1 : 0,
        }}
        onMouseEnter={isOpen ? () => setIsHoverActive(true) : undefined}
      >
        <MenuItem
          activeItem={currentActive}
          isDesktop
          onClose={closeActiveMenu}
        />
      </Box>
    </>
  );
};
